import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	TemplateRef,
	ViewChild,
	WritableSignal,
	computed,
	input,
	signal
} from '@angular/core';
import { UserApi } from 'api/user/user-api.js';
import { WorkoutApi } from 'api/workout/workout-api.js';
import { ExerciseDb } from 'lib/cosmos/exercise/exerciseDb.js';
import { SessionDb } from 'lib/cosmos/session/sessionDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { WorkoutDb } from 'lib/cosmos/workout/workoutDb.js';
import { arrayHelper } from 'lib/helpers/array-helper.class.js';
import { isNotUndefined } from 'lib/helpers/array.utils.js';
import { cloneHelper } from 'lib/helpers/clone-helper.class.js';
import { guidHelper } from 'lib/helpers/guid-helper.class.js';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { observableHelper } from 'lib/helpers/observable-helper.class.js';
import { pluralHelper } from 'lib/helpers/plural.helper.class.js';
import { stringHelper } from 'lib/helpers/string-helper.class.js';
import { LiteralUnion } from 'lib/types/lib.types.js';
import { IButton } from 'lib/ui/buttons/index.js';
import { DialogFormFieldType, IDialogButtonsContext, SelectionDialogItem } from 'lib/ui/dialog/index.js';
import { SelectItemField } from 'lib/ui/form/fields/select-item-field.component.js';
import { ExerciseListItem } from 'lib/ui/list/select-exercise-list.component.js';
import { SimpleTableItem } from 'lib/ui/simple-table/index.js';
import { InfoTableItem } from 'lib/ui/simple-table/info-table.component.js';
import { IImage } from 'lib/ui/ui-models.js';
import { Observable, Subject, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';
import { IViewOrEditWorkoutPlanRouteData } from 'src/app/routes/trainer/workout-routes.js';
import { match } from 'ts-pattern';
import { ExerciseUtilsService } from '../exercises/services/exercise-utils.service.js';
import { sessionsHelper } from '../sessions/sessions.helper.js';

type ExtendedSet = {
	readonly originalSet: WorkoutDb.IWorkoutExerciseSet;
	readonly textSignal: WritableSignal<string | undefined>;
	readonly buttons: readonly IButton[];
};

type ExtendedWorkoutExercise = {
	readonly id: string;
	readonly originalExercise: WorkoutDb.IWorkoutExercise;
	readonly linkedExercise: WorkoutExercise | undefined;
	readonly name: string;
	readonly subtitle: string | undefined;
	readonly nameWithVariantIndex: string;
	readonly variantIndex: string;
	readonly previewImages: readonly IImage[];
	readonly images: readonly IImage[];
	readonly setsTitle: string;
	readonly buttons: readonly IButton[];
	readonly idAsSignal: WritableSignal<string>;
	readonly extendedSets: readonly ExtendedSet[];
	readonly aboutExerciseInfoTableItems: readonly InfoTableItem[];
};

type ExtendedWorkoutExerciseContainer = {
	readonly originalContainer: WorkoutDb.IWorkoutExerciseContainer;
	readonly domId: string;
	readonly title: string;
	readonly exercises: readonly ExtendedWorkoutExercise[];
	readonly buttons: readonly IButton[];
	readonly activeExercise?: ExtendedWorkoutExercise;
	readonly variantSelectionItems: readonly SelectItemField<ExtendedWorkoutExercise>[];
	readonly typeSignal: WritableSignal<WorkoutDb.WorkoutExerciseType>;
};

type WorkoutSaveConfig = {
	readonly scrollToContainerId?: string;
};

type ActiveExercises = {
	[containerId: string]: string | undefined;
};

type EditTextDialog = {
	readonly text?: string;
};

type WorkoutExercise = {
	readonly id: string;
	readonly name: string;
	readonly images: readonly IImage[];
	readonly equipment?: LiteralUnion<ExerciseDb.Equipment>;
	readonly difficulty?: ExerciseDb.DifficultyLevel;
	readonly videoUrl?: string;
	readonly instructions?: string;
	readonly isGlobal: boolean;
	readonly targetMuscleGroup?: ExerciseDb.TargetMuscleGroup;
	readonly forceType?: ExerciseDb.ForceType;
	readonly laterality?: ExerciseDb.Laterality;
};

type AddExerciseLocation = 'top' | 'bottom';

@Component({
	templateUrl: './view-or-edit-workout.component.html',
	selector: 'mod-view-or-edit-workout',
	styleUrls: ['./view-or-edit-workout.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ViewOrEditWorkoutComponent extends TypeCoreComponent<IViewOrEditWorkoutPlanRouteData> implements OnInit {
	public readonly isPdfView = input<boolean>(false);

	@ViewChild('editSessionTemplate', { static: true }) editSessionTemplate!: TemplateRef<any>;
	@ViewChild('newExerciseTemplate', { static: true }) newExerciseTemplate!: TemplateRef<any>;

	private readonly _workoutSave = new Subject<WorkoutSaveConfig | undefined>();
	public readonly setMaxLength: number = 100;

	public readonly isEdit = computed<boolean>(() => {
		return this.sharedData()?.data?.isWorkoutDesigner ?? false;
	});

	private readonly allWorkouts = signal<readonly WorkoutDb.IWorkoutItem[] | undefined>(undefined);

	private readonly activeExerciseIds = signal<ActiveExercises>({});
	public readonly exerciseTypeSelectionItems: readonly SelectItemField<WorkoutDb.WorkoutExerciseType>[] = [
		{
			title: 'Variants',
			value: 'variant',
			data: 'variant'
		},
		{
			title: 'Superset',
			value: 'superset',
			data: 'superset'
		}
	];

	private readonly allExercises = signal<readonly WorkoutExercise[]>([]);

	public readonly workoutOrActivityName = computed<string | undefined>(() => {
		const workout = this.workout();
		if (!workout) {
			return undefined;
		}
		return workout.type === 'activity' ? 'Activity' : 'Workout';
	});

	public readonly exercisesDividerTitle = computed<string | undefined>(() => {
		const workout = this.workout();
		if (!workout || workout.type !== 'workout') {
			return undefined;
		}

		return match(workout.exerciseContainers.length)
			.returnType<string>()
			.when(
				(m) => m === 1,
				(m) => `1 exercise`
			)
			.when(
				(m) => m > 1,
				(m) => `${m} exercises`
			)
			.when(
				(m) => m === 0,
				() => 'No exercises'
			)
			.otherwise(() => 'No exercises');
	});

	public readonly workoutDetailsSimpleTableItems = computed<readonly SimpleTableItem[] | undefined>(() => {
		const workout = this.workout();
		if (!workout) {
			return undefined;
		}

		const items: readonly SimpleTableItem[] = [
			{
				title: `Recommended length`,
				value: workout.recommendedLength,
				valueType: 'text',
				// iconType: 'length',
				buttons: !this.isEdit()
					? []
					: [
							{
								iconOnly: true,
								title: 'Edit',
								icon: 'edit',
								click: () => {
									this.openEditDialog({
										workout,
										fieldType: 'text',
										getProp: (workout) => workout.recommendedLength,
										setProp: (workout, value) => {
											workout.recommendedLength = value;
										},
										title: 'Recommended length'
									});
								}
							}
					  ]
			},
			{
				title: `Warm up`,
				valueType: 'html',
				value: workout.warmUp,
				// iconType: 'warmUp',

				buttons: !this.isEdit()
					? []
					: [
							{
								iconOnly: true,
								title: 'Edit',
								icon: 'edit',
								click: () => {
									this.openEditDialog({
										workout,
										fieldType: 'textarea',
										getProp: (workout) => workout.warmUp,
										setProp: (workout, value) => {
											workout.warmUp = value;
										},
										title: 'Warm up'
									});
								}
							}
					  ]
			},
			{
				title: `Cooldown`,
				valueType: 'html',
				value: workout.cooldown,
				// iconType: 'cooldown',
				buttons: !this.isEdit()
					? []
					: [
							{
								iconOnly: true,
								title: 'Edit',
								icon: 'edit',
								click: () => {
									this.openEditDialog({
										workout,
										fieldType: 'textarea',
										getProp: (workout) => workout.cooldown,
										setProp: (workout, value) => {
											workout.cooldown = value;
										},
										title: 'Cooldown'
									});
								}
							}
					  ]
			}
		];

		// return undefined if all items are empty and workout is not editable
		if (!this.isEdit() && items.every((m) => m.value === undefined)) {
			return undefined;
		}

		return items;
	});

	public readonly workoutExercisesLoaded = signal<boolean>(false);
	public readonly workout = signal<WorkoutDb.IWorkoutItem | undefined>(undefined);
	public readonly filteredExercises = signal<readonly ExerciseDb.IExerciseItem[]>([]);
	public readonly extendedWorkoutExerciseContainers = computed<
		readonly ExtendedWorkoutExerciseContainer[] | undefined
	>(() => {
		const workout = this.workout();
		const activeExerciseIds = this.activeExerciseIds();

		if (!workout) {
			return undefined;
		}

		return this.getExtendedWorkoutContainers({
			workout: workout,
			activeExercises: activeExerciseIds,
			allExercises: this.allExercises()
		});
	});

	private readonly showAddExerciseButton = computed<boolean>(() => {
		if (!this.isEdit() || !this.workoutExercisesLoaded()) {
			return false;
		}

		const exercises = this.allExercises();

		if (exercises.length) {
			return true;
		}
		return false;
	});

	public readonly sessionEditUserItem = computed<UserDb.IUserItem | undefined>(() => {
		const sharedData = this.sharedData();
		if (sharedData?.data?.clientData?.client) {
			return sharedData.data.clientData.client;
		}

		return undefined;
	});
	public readonly selectedSessionItem = signal<SessionDb.ISessionItem | undefined>(undefined);
	public readonly viewMainButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData) {
			return [];
		}

		const selectedSessionItem = this.selectedSessionItem();
		const buttons: IButton[] = [];

		const sessionUserItem = this.sessionEditUserItem();

		if (sessionUserItem) {
			buttons.push({
				title: selectedSessionItem
					? `Continue session (${this.dependencies.timeService.formatDayAndMonth(selectedSessionItem.date)})`
					: 'Start new session',
				buttonType: 'primary',
				buttonSize: 'large',
				click: () => {
					if (sharedData?.data.workout) {
						const exercises: readonly ExerciseDb.IExerciseItem[] =
							super.getLinkedItemsByType<ExerciseDb.IExerciseItem>('Exercise');

						if (selectedSessionItem) {
							this.openEditSessionDialog();
						} else {
							this.createSession({
								user: sessionUserItem,
								workout: sharedData.data.workout,
								stateItems: sessionsHelper.getStateItems({
									workout: sharedData.data.workout,
									availableExercises: exercises
								})
							});
						}
					}
				}
			});
		}

		return buttons;
	});

	public readonly addExerciseToTopButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();
		const exerciseContainers = this.extendedWorkoutExerciseContainers();

		if (!sharedData || !exerciseContainers) {
			return [];
		}

		const buttons: IButton[] = [];

		if (this.showAddExerciseButton()) {
			buttons.push({
				buttonSize: 'full',
				title: exerciseContainers.length === 0 ? `Add exercise` : `Add exercise to top`,
				icon: exerciseContainers.length === 0 ? 'add' : 'add',
				buttonType: 'primary',
				click: () => {
					this.openSelectExerciseDialog({
						location: 'top',
						container: undefined,
						replaceExercise: undefined
					});
				}
			});
		}

		return buttons;
	});

	public readonly addExerciseToBottomButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();
		const exerciseContainers = this.extendedWorkoutExerciseContainers();

		if (!sharedData || !exerciseContainers) {
			return [];
		}

		if (exerciseContainers.length === 0) {
			return [];
		}

		const buttons: IButton[] = [];

		if (this.showAddExerciseButton()) {
			buttons.push({
				buttonSize: 'full',
				title: `Add exercise to bottom`,
				icon: 'add',
				buttonType: 'primary',
				click: () => {
					this.openSelectExerciseDialog({
						location: 'bottom',
						container: undefined,
						replaceExercise: undefined
					});
				}
			});
		}

		return buttons;
	});

	public readonly editQuickActionButtons = computed<readonly IButton[]>(() => {
		const sharedData = this.sharedData();

		if (!sharedData || !this.isEdit()) {
			return [];
		}

		const buttons: IButton[] = [
			...this.addExerciseToTopButtons(),
			{
				title: 'Create exercise',
				buttonSize: 'default',
				buttonType: 'basic',
				icon: 'add',
				click: () => {
					this.openCreateCustomExerciseDialog();
				}
			}
		];
		const workoutExercisesCount = this.workout()?.exerciseContainers?.length ?? 0;

		if (workoutExercisesCount > 0) {
			buttons.push({
				title: `Remove exercises from workout`,
				icon: 'remove',
				buttonSize: 'default',
				buttonType: 'basic',
				confirm: {
					title: 'Remove exercises',
					text: `Are you sure to remove all (${workoutExercisesCount}) exercises from workout?`,
					confirmMode: 'default'
				},
				click: () => {
					this.removeAllExercises();
				}
			});
		}

		buttons.push({
			title: 'Copy exercises from another template',
			buttonType: 'basic',
			icon: 'clone',
			buttonSize: 'default',
			click: () => {
				this.openCopyFromWorkoutDialog();
			}
		});

		return buttons;
	});

	public readonly editNotesButton = computed<IButton | undefined>(() => {
		if (!this.isEdit()) {
			return undefined;
		}

		const workout = this.workout();

		if (!workout) {
			return undefined;
		}

		return {
			icon: 'edit',
			iconOnly: true,
			title: 'Edit notes',
			click: () => {
				this.openEditDialog({
					workout: workout,
					fieldType: 'textarea',
					getProp: (workout) => workout.notes,
					setProp: (workout, value) => {
						workout.notes = value;
					},
					title: 'Notes'
				});
			}
		};
	});

	constructor(private readonly exerciseUtilsService: ExerciseUtilsService) {
		super();

		this.subscribeToWorkoutSave();

		this.dataInit = () => {
			if (this.authUser()?.userType === 'trainer') {
				// only load exercises if user is trainer
				this.loadWorkoutExercises();
			}
		};

		this.sharedDataInit = (data) => {
			this.workout.set(data.data?.workout);
			this.selectedSessionItem.set(data.data.sessionForDate);
			this.allExercises.update((m) =>
				this.filterUniqueExercises([
					...m,
					...linkedItemsHelper
						.getLinkedItemsByType<ExerciseDb.IExerciseItem>(data.linkedItems ?? [], 'Exercise')
						.map(this.mapExerciseItemToWorkoutExercise)
				])
			);
		};
	}

	handleCreateExercise(exercise: ExerciseDb.IExerciseItem): void {
		this.allExercises.update((m) => {
			return this.filterUniqueExercises([...m, this.mapExerciseItemToWorkoutExercise(exercise)]);
		});

		this.dependencies.dialogService.closeAllDialogs();
		this.handleAddExercise('top', this.mapExerciseItemToWorkoutExercise(exercise), undefined);
	}

	handleSetChanged(value: string | undefined, exercise: ExtendedWorkoutExercise, set: ExtendedSet): void {
		set.originalSet.text = value;
		this._workoutSave.next({});
	}

	handleFastSetChanged(value: string | undefined, exercise: ExtendedWorkoutExercise, set: ExtendedSet): void {
		set.originalSet.text = value;
	}

	handleRemoveContainer(container: WorkoutDb.IWorkoutExerciseContainer): void {
		const workout = this.workout();
		if (!workout) {
			return;
		}

		this.dependencies.dialogService.openConfirmationDialog({
			mode: 'delete',
			text: 'Are you sure to remove this exercise?',
			confirm: () => {
				workout.exerciseContainers = workout.exerciseContainers.filter((m) => m.id !== container.id);
				this.updateWorkout();
			}
		});
	}

	handleAddSet(exercise: ExtendedWorkoutExercise): void {
		exercise.originalExercise.sets.push({
			id: guidHelper.shortGuid(),
			text: ``
		});

		this.updateWorkout();
	}

	handleSetExerciseType(container: ExtendedWorkoutExerciseContainer, type: string | undefined): void {
		const typeMapped = type as WorkoutDb.WorkoutExerciseType;
		container.originalContainer.type = typeMapped;

		this.updateWorkout();
	}

	handleSetActiveExercise(container: ExtendedWorkoutExerciseContainer, exerciseId: string | undefined): void {
		this.activeExerciseIds.update((value) => {
			value[container.originalContainer.id] = exerciseId;

			return cloneHelper.clone(value);
		});
	}

	getExerciseContainerButtonThreshold(isFirst: boolean, isLast: boolean, containersCount: number): number {
		if (containersCount === 1) {
			return 1;
		}

		if (isFirst || isLast) {
			return 2;
		}

		return 3;
	}

	private filterUniqueExercises(exercises: readonly WorkoutExercise[]): readonly WorkoutExercise[] {
		const exercisesMap: Map<string, WorkoutExercise> = exercises.reduce((acc, curr) => {
			acc.set(curr.id, curr);
			return acc;
		}, new Map<string, WorkoutExercise>());
		return Array.from(exercisesMap.values());
	}

	private mapSimplifiedExerciseToWorkoutExercise(
		exercise: WorkoutApi.SimplifiedExercise,
		maps: WorkoutApi.SimplifiedExerciseMap
	): WorkoutExercise {
		return {
			id: exercise.id,
			name: exercise.n,
			equipment: exercise.e || exercise.e === 0 ? maps.equipment[exercise.e] : undefined,
			difficulty: exercise.d || exercise.d === 0 ? maps.difficulty[exercise.d] : undefined,
			videoUrl: exercise.v,
			instructions: exercise.i,
			isGlobal: exercise.g,
			targetMuscleGroup: exercise.m || exercise.m === 0 ? maps.targetMuscleGroup[exercise.m] : undefined,
			forceType: exercise.f || exercise.f === 0 ? maps.forceType[exercise.f] : undefined,
			laterality: exercise.l || exercise.l === 0 ? maps.laterality[exercise.l] : undefined,
			images: exercise.img ?? []
		};
	}

	private mapExerciseItemToWorkoutExercise(exercise: ExerciseDb.IExerciseItem): WorkoutExercise {
		return {
			id: exercise.id,
			name: exercise.system.name,
			images: exercise.images?.map((m) => m.image).filter(isNotUndefined) ?? [],
			equipment: exercise.equipment,
			videoUrl: exercise.videoUrl,
			instructions: exercise.instructions,
			isGlobal: exercise.system.isGlobal,
			targetMuscleGroup: exercise.targetMuscleGroup,
			forceType: exercise.forceType,
			laterality: exercise.laterality
		};
	}

	private handleRemoveExercise(
		workout: WorkoutDb.IWorkoutItem,
		container: WorkoutDb.IWorkoutExerciseContainer,
		exercise: WorkoutDb.IWorkoutExercise
	): void {
		container.exercises = container.exercises.filter((m) => m.id !== exercise.id);

		if (!container.exercises.length) {
			// remove container as a whole
			workout.exerciseContainers = workout.exerciseContainers.filter((m) => m.id !== container.id);
		}

		this.updateWorkout();
	}

	private handleRemoveExerciseSet(
		workout: WorkoutDb.IWorkoutItem,
		exercise: WorkoutDb.IWorkoutExercise,
		set: WorkoutDb.IWorkoutExerciseSet
	): void {
		exercise.sets = exercise.sets.filter((m) => m.id !== set.id);
		this.updateWorkout();
	}

	private updateWorkout(saveConfig?: WorkoutSaveConfig): void {
		const workout = this.workout();

		if (workout) {
			this.workout.set(cloneHelper.clone(workout));
			this._workoutSave.next(saveConfig);
		}
	}

	private handleCloneExerciseSet(
		workout: WorkoutDb.IWorkoutItem,
		setIndex: number,
		exercise: WorkoutDb.IWorkoutExercise,
		set: WorkoutDb.IWorkoutExerciseSet
	): void {
		const clonedSet = cloneHelper.clone(set);
		clonedSet.id = guidHelper.shortGuid();

		exercise.sets.splice(setIndex, 0, clonedSet);

		this.updateWorkout();
	}
	private canMoveDown(workout: WorkoutDb.IWorkoutItem, index: number): boolean {
		if (!workout.exerciseContainers.length) {
			return false;
		}

		if (index === workout.exerciseContainers.length - 1) {
			return false;
		}

		return true;
	}

	private canMoveUp(workout: WorkoutDb.IWorkoutItem, index: number): boolean {
		if (!workout.exerciseContainers.length) {
			return false;
		}

		if (index <= 0) {
			return false;
		}

		return true;
	}

	private handleMove(
		workout: WorkoutDb.IWorkoutItem,
		container: WorkoutDb.IWorkoutExerciseContainer,
		direction: 'up' | 'down'
	): void {
		const containers = workout.exerciseContainers;
		const index = containers.findIndex((m) => m.id === container.id);

		if (direction === 'up') {
			if (!this.canMoveUp(workout, index)) {
				return;
			}

			const previousIndex: number = index;
			const newIndex: number = index - 1;

			arrayHelper.moveItemInArray(containers, previousIndex, newIndex);
		} else if (direction === 'down') {
			if (!this.canMoveDown(workout, index)) {
				return;
			}

			const previousIndex: number = index;
			const newIndex: number = index + 1;

			arrayHelper.moveItemInArray(containers, previousIndex, newIndex);
		} else {
			throw Error(`Invalid direction '${direction}'`);
		}

		this.updateWorkout();
	}

	private openCopyFromWorkoutDialog(): void {
		const allWorkouts = this.allWorkouts();
		if (allWorkouts) {
			this.openSelectWorkoutDialog(allWorkouts);
		} else {
			super.subscribeToObservable(
				this.getAllWorkouts().pipe(
					map((allWorkouts) => {
						this.allWorkouts.set(allWorkouts);
						this.openSelectWorkoutDialog(allWorkouts);
					})
				)
			);
		}
	}

	private getAllWorkouts(): Observable<readonly WorkoutDb.IWorkoutItem[]> {
		return this.dependencies.httpService.listOwned<WorkoutDb.IWorkoutItem>('workout').pipe(
			map((response) => {
				return response.data;
			})
		);
	}

	private openSelectWorkoutDialog(workouts: readonly WorkoutDb.IWorkoutItem[]): void {
		this.dependencies.dialogService.openSelectionDialog({
			size: 'default',
			title: 'Select workout',
			subtitle: 'Exercises will be copied from selected workout',
			avatarMode: 'preferImage',
			items: workouts
				.filter((m) => m.id !== this.workout()?.id)
				.map((workout) => {
					const selectionItem: SelectionDialogItem<WorkoutDb.IWorkoutItem> = {
						data: workout,
						id: workout.id,
						title: workout.system.name,
						avatar: workout?.avatar?.[0]?.image,
						subtitle: pluralHelper.getPluralText({
							count: workout.exerciseContainers.length,
							m0: 'No exercises',
							m1: '1 exercise',
							m5: `${workout.exerciseContainers.length} exercises`
						})
					};

					return selectionItem;
				}),
			select: (item) => {
				const selectedWorkout = item;
				const workout = this.workout();

				if (workout) {
					workout.exerciseContainers = selectedWorkout.exerciseContainers;
					this.updateWorkout();
				}
			}
		});
	}

	private openSelectExerciseDialog({
		location,
		container,
		replaceExercise
	}: {
		readonly location: AddExerciseLocation;
		readonly container: WorkoutDb.IWorkoutExerciseContainer | undefined;
		readonly replaceExercise: WorkoutDb.IWorkoutExercise | undefined;
	}): void {
		this.dependencies.dialogService.openDialogWithSections({
			dialogId: 'openSelectExerciseDialog',
			sections: [
				{
					id: 'allExercises',
					name: 'All exercises',
					dialog: (builder) => {
						return builder.exerciseDialog({
							buttons: (context) => this.getSelectExerciseDialogButtons(context),
							items: this.getExerciseDialogSelectionItems('all'),
							select: (item) => {
								if (replaceExercise) {
									this.handleReplaceExercise(item, replaceExercise);
								} else {
									this.handleAddExercise(location, item, container);
								}
							}
						});
					}
				},
				{
					id: 'custom',
					name: 'Custom exercises',
					dialog: (builder) => {
						return builder.exerciseDialog({
							buttons: (context) => this.getSelectExerciseDialogButtons(context),
							items: this.getExerciseDialogSelectionItems('custom'),
							select: (item) => {
								if (replaceExercise) {
									this.handleReplaceExercise(item, replaceExercise);
								} else {
									this.handleAddExercise(location, item, container);
								}
							}
						});
					}
				}
			]
		});
	}

	private openCreateCustomExerciseDialog(): void {
		this.dependencies.dialogService.openDialog({
			title: 'Create exercise',
			subtitle: 'You may create as many custom exercises as you need',
			size: 'large',
			template: () => this.newExerciseTemplate
		});
	}

	private getSelectExerciseDialogButtons(context: IDialogButtonsContext): readonly IButton[] {
		return [
			{
				buttonSize: 'default',
				buttonType: 'basic',
				icon: 'add',
				title: 'Create exercise',
				click: () => {
					context.closeDialog();
					this.openCreateCustomExerciseDialog();
				}
			}
		];
	}

	private removeAllExercises(): void {
		const workout = this.workout();
		if (!workout) {
			return;
		}

		workout.exerciseContainers = [];
		this.updateWorkout();
	}

	private handleReplaceExercise(
		exercise: WorkoutExercise | ExerciseListItem,
		replaceExercise: WorkoutDb.IWorkoutExercise
	): void {
		replaceExercise.exerciseId = exercise.id;

		this.updateWorkout();
	}

	private handleAddExercise(
		location: AddExerciseLocation,
		exercise: WorkoutExercise | ExerciseListItem,
		container: WorkoutDb.IWorkoutExerciseContainer | undefined
	): void {
		const workout = this.workout();
		if (!workout) {
			return;
		}

		let newContainerId: string | undefined = undefined;

		const exerciseContainers: WorkoutDb.IWorkoutExerciseContainer[] = workout.exerciseContainers ?? [];

		const newExercise: WorkoutDb.IWorkoutExercise = {
			id: guidHelper.shortGuid(),
			exerciseId: exercise.id,
			sets: [
				{
					id: guidHelper.shortGuid()
				}
			]
		};

		if (container) {
			if (!container.exercises.length) {
				container.exercises = [];
			}

			container.exercises.push(newExercise);
		} else {
			// add new exercise with container
			newContainerId = guidHelper.shortGuid();

			const newExerciseContainer: WorkoutDb.IWorkoutExerciseContainer = {
				id: newContainerId,
				type: 'default',
				exercises: [newExercise]
			};

			if (location === 'top') {
				exerciseContainers.unshift(newExerciseContainer);
			} else {
				exerciseContainers.push(newExerciseContainer);
			}
		}

		this.updateWorkout({
			scrollToContainerId: newContainerId
		});
	}

	private getExerciseDialogSelectionItems(type: 'custom' | 'global' | 'all'): readonly ExerciseListItem[] {
		const allExercises = this.allExercises();
		if (!allExercises.length) {
			return [];
		}

		const exercisesToSelectFrom = allExercises.filter((m) => {
			if (type === 'all') {
				return true;
			}

			if (type === 'custom') {
				return !m.isGlobal;
			}

			// global
			return m.isGlobal;
		});

		return exercisesToSelectFrom.map<ExerciseListItem>((m) => {
			return {
				id: m.id,
				name: m.name,
				equipment: m.equipment,
				difficulty: m.difficulty,
				videoUrl: m.videoUrl,
				instructions: m.instructions,
				isGlobal: m.isGlobal,
				images: m.images,
				targetMuscleGroup: m.targetMuscleGroup,
				forceType: m.forceType,
				laterality: m.laterality
			};
		});
	}

	private subscribeToWorkoutSave(): void {
		super.subscribeToObservable(
			this._workoutSave.pipe(
				observableHelper.saveDebounceTime({ isMobileView: this.isMobileView() }),
				map((config) => {
					this.saveWorkout(config);
				})
			)
		);
	}

	private loadWorkoutExercises(): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.get<WorkoutApi.GetWorkoutExercisesResponse>((builder) =>
					builder.withCustomApiRoute('workout', 'getWorkoutExercises').build()
				)
				.pipe(
					map((response) => {
						this.allExercises.update((m) =>
							this.filterUniqueExercises([
								...m,
								...response.data.exercises.map((m) =>
									this.mapSimplifiedExerciseToWorkoutExercise(m, response.data.maps)
								)
							])
						);
						this.workoutExercisesLoaded.set(true);
					})
				)
		);
	}

	private getExerciseButtons(data: {
		readonly containerIndex: number;
		readonly workout: WorkoutDb.IWorkoutItem;
		readonly container: WorkoutDb.IWorkoutExerciseContainer;
		readonly exercise: WorkoutDb.IWorkoutExercise;
		readonly linkedExercise: WorkoutExercise | undefined;
	}): readonly IButton[] {
		const removeVariantButton: IButton | undefined =
			this.isEdit() && data.container.exercises.length > 1
				? {
						iconOnly: true,
						tooltip: `Remove selected variant`,
						title: `Remove selected variant`,
						buttonSize: 'default',
						icon: 'delete',
						buttonType: 'delete',
						confirm: {
							confirmMode: 'delete',
							text: `Are you sure to remove variant '${data.linkedExercise?.name}'?`
						},
						click: () => {
							this.handleRemoveExercise(data.workout, data.container, data.exercise);
						}
				  }
				: undefined;

		const moveDownButton: IButton | undefined =
			this.isEdit() && this.canMoveDown(data.workout, data.containerIndex)
				? {
						title: 'Move down',
						buttonSize: 'default',
						buttonType: 'basic',
						iconOnly: true,
						click: () => {
							this.handleMove(data.workout, data.container, 'down');
						},
						icon: 'arrowDown'
				  }
				: undefined;

		const moveUpButton: IButton | undefined =
			this.isEdit() && this.canMoveUp(data.workout, data.containerIndex)
				? {
						title: 'Move up',
						buttonSize: 'default',
						buttonType: 'basic',
						iconOnly: true,
						click: () => {
							this.handleMove(data.workout, data.container, 'up');
						},
						icon: 'arrowUp'
				  }
				: undefined;

		const addVariantButton: IButton | undefined = this.isEdit()
			? {
					buttonSize: 'default',
					buttonType: 'basic',
					iconOnly: true,
					title: `Add variant`,
					click: () => {
						this.openSelectExerciseDialog({
							location: 'top',
							container: data.container,
							replaceExercise: undefined
						});
					},
					icon: 'add'
			  }
			: undefined;

		const replaceExerciseButton: IButton | undefined = this.isEdit()
			? {
					buttonSize: 'default',
					buttonType: 'basic',
					iconOnly: true,
					title: `Replace exercise`,
					click: () => {
						this.openSelectExerciseDialog({
							location: 'top',
							container: data.container,
							replaceExercise: data.exercise
						});
					},
					icon: 'replace'
			  }
			: undefined;

		const removeExerciseButton: IButton | undefined = this.isEdit()
			? {
					buttonSize: 'default',
					buttonType: 'basic',
					iconOnly: true,
					title: `Remove exercise`,
					click: () => {
						this.handleRemoveContainer(data.container);
					},
					icon: 'delete'
			  }
			: undefined;

		return [
			moveDownButton,
			moveUpButton,
			replaceExerciseButton,
			addVariantButton,
			removeExerciseButton,
			removeVariantButton
		].filter(isNotUndefined);
	}

	private getExtendedWorkoutContainers(data: {
		readonly workout: WorkoutDb.IWorkoutItem;
		readonly activeExercises: ActiveExercises;
		readonly allExercises: readonly WorkoutExercise[];
	}): readonly ExtendedWorkoutExerciseContainer[] {
		const containers = data.workout.exerciseContainers ?? [];

		return containers
			.filter((m) => m.exercises.length)
			.map((container, containerIndex) => {
				let exerciseIndex: number = 0;
				const extendedExercises: ExtendedWorkoutExercise[] = container.exercises.map((exercise) => {
					const linkedExercise = data.allExercises.find((m) => m.id === exercise.exerciseId);
					const exerciseButtons: readonly IButton[] = this.getExerciseButtons({
						workout: data.workout,
						container: container,
						exercise: exercise,
						containerIndex: containerIndex,
						linkedExercise: linkedExercise
					});

					const variantIndex = stringHelper.getAlphabetCharByIndex(exerciseIndex).toUpperCase();
					const variantPrefix = container.exercises.length > 1 ? `${variantIndex}. ` : '';

					let setIndex: number = 0;
					const extendedExercise: ExtendedWorkoutExercise = {
						id: exercise.id,
						originalExercise: exercise,
						setsTitle: match(exercise)
							.returnType<string>()
							.when(
								(m) => m.sets.length === 0,
								(m) => `No sets`
							)
							.when(
								(m) => m.sets.length === 1,
								(m) => `1 set`
							)
							.when(
								(m) => m.sets.length > 1,
								(m) => `${m.sets.length} sets`
							)
							.otherwise((m) => `No sets`),
						linkedExercise: linkedExercise,
						extendedSets: exercise.sets.map((set) => {
							const extendedSet: ExtendedSet = {
								originalSet: set,
								textSignal: signal(set.text),
								buttons: [
									{
										title: 'Clone',
										icon: 'clone',
										iconOnly: true,
										click: () => {
											this.handleCloneExerciseSet(data.workout, setIndex, exercise, set);
										}
									},
									{
										title: 'Remove',
										icon: 'remove',
										iconOnly: true,
										click: () => {
											this.handleRemoveExerciseSet(data.workout, exercise, set);
										}
									}
								]
							};
							setIndex++;
							return extendedSet;
						}),
						idAsSignal: signal(exercise.id),
						buttons: exerciseButtons,
						images: linkedExercise?.images ?? [],
						previewImages: (linkedExercise?.images ?? []).slice(0, 2),
						variantIndex: variantIndex,
						name: `${super.getRequiredItemName(linkedExercise?.name)}`,
						subtitle: undefined,
						nameWithVariantIndex: `${variantPrefix}${super.getRequiredItemName(linkedExercise?.name)}`,
						aboutExerciseInfoTableItems: linkedExercise
							? this.getExerciseInfoTableItems(linkedExercise)
							: []
					};

					exerciseIndex++;

					return extendedExercise;
				});

				let title: string = '';

				if (extendedExercises.length === 0) {
					title = `Remove this exercise`;
				} else if (extendedExercises.length === 1) {
					// no need for title
				} else {
					if (container.type === 'superset') {
						title = `Perform supersets for all exercises`;
					} else {
						const chooseText: string = stringHelper.joinWithLastSeparator(
							`, `,
							` or `,
							extendedExercises.map((m) => m.variantIndex)
						);
						title = `Choose one (${chooseText})`;
					}
				}

				const exerciseButtons: readonly IButton[] = [];

				if (this.isEdit()) {
					if (containers.length > 1) {
					}
				}

				let activeExercise: ExtendedWorkoutExercise | undefined = undefined;
				const rememberedActiveExerciseId = data.activeExercises[container.id];

				if (rememberedActiveExerciseId) {
					activeExercise = extendedExercises.find((m) => m.id === rememberedActiveExerciseId);
				}

				if (extendedExercises.length && !activeExercise) {
					activeExercise = extendedExercises[0];
				}

				const extendedContainer: ExtendedWorkoutExerciseContainer = {
					domId: `dom_container_${container.id}`,
					typeSignal: signal(container.type),
					title: `${title}`,
					originalContainer: container,
					activeExercise: activeExercise,
					exercises: extendedExercises,
					variantSelectionItems: extendedExercises.map((exercise) => {
						const item: SelectItemField<ExtendedWorkoutExercise> = {
							data: exercise,
							title: exercise.nameWithVariantIndex,
							value: exercise.id,
							avatar: exercise.images?.[0],
							avatarLocation: 'end'
						};

						return item;
					}),
					buttons: exerciseButtons
				};

				return extendedContainer;
			});
	}

	private getExerciseInfoTableItems(exercise: WorkoutExercise): readonly InfoTableItem[] {
		const instructionsItem: InfoTableItem | undefined = exercise.instructions?.length
			? {
					title: 'Instructions',
					value: `View`,
					valueClick: () => this.openInstructionsDialog(exercise)
			  }
			: undefined;

		return [
			{ title: 'Equipment', value: exercise.equipment },
			{ title: 'Difficulty', value: exercise.difficulty },
			{ title: 'Muscle group', value: exercise.targetMuscleGroup },
			{ title: 'Force type', value: exercise.forceType },
			instructionsItem
		].filter(isNotUndefined);
	}

	private openInstructionsDialog(exercise: WorkoutExercise): void {
		this.exerciseUtilsService.openExerciseInstructionDialog({
			images: exercise.images,
			equipment: exercise.equipment,
			categories: [],
			videoUrl: exercise.videoUrl,
			instructions: exercise.instructions,
			name: exercise.name,
			targetMuscleGroup: exercise.targetMuscleGroup
		});
	}

	private saveWorkout(config: WorkoutSaveConfig | undefined): void {
		const workout = this.workout();
		const sharedData = this.sharedData();

		const clientData = sharedData?.data?.clientData;

		if (!workout || !sharedData) {
			return;
		}

		// remove empty exercises in case there are any
		workout.exerciseContainers = workout.exerciseContainers.filter((m) => m.exercises.length > 0);

		if (!clientData) {
			// save regular workout
			super.subscribeToObservable(
				this.dependencies.httpService
					.post<WorkoutDb.IWorkoutItem, Partial<WorkoutDb.IWorkoutItem>>((builder) =>
						builder.withCustomApiRoute('workout', 'edit').withData(workout).build()
					)
					.pipe(
						map((response) => {
							super.notifySaved('Workout');
							super.updateSharedData({
								linkedItems: this.sharedData()?.linkedItems,
								data: {
									workoutType: sharedData.data.workoutType,
									workout: response.data,
									isWorkoutDesigner: sharedData.data.isWorkoutDesigner,
									clientData: sharedData.data.clientData,
									sessionForDate: this.selectedSessionItem()
								}
							});

							if (config?.scrollToContainerId) {
								this.scrollToContainer(config.scrollToContainerId);
							}
						})
					)
			);
		} else {
			// save in client
			const trainingContainers: readonly UserDb.IUserTrainingContainer[] =
				clientData.client.training?.containers ?? [];
			const trainingContainer = trainingContainers.find((m) => m.id === clientData.trainingContainer.id);

			if (!trainingContainer) {
				throw new Error(`Invalid training container with id '${clientData.trainingContainer.id}'`);
			}

			const containerItemIndex = trainingContainer.items.findIndex((m) => m.id === clientData.trainingItem.id);

			// replace workout
			trainingContainer.items[containerItemIndex].workout = workout;

			// save user
			super.subscribeToObservable(
				this.dependencies.httpService
					.post<UserDb.IUserItem, UserApi.IEditTrainingPlanRequest>((builder) =>
						builder
							.withCustomApiRoute('user', 'editUserTraining')
							.withId(clientData.client.id)
							.withData({
								title: clientData.client.training?.title ?? 'default',
								trainingContainers: trainingContainers
							})
							.build()
					)
					.pipe(
						map((response) => {
							super.notifySaved('Workout');
							super.updateSharedData({
								linkedItems: this.sharedData()?.linkedItems,
								data: {
									workoutType: sharedData.data.workoutType,
									workout: workout,
									isWorkoutDesigner: sharedData.data.isWorkoutDesigner,
									clientData: sharedData?.data?.clientData,
									sessionForDate: this.selectedSessionItem()
								}
							});

							if (config?.scrollToContainerId) {
								this.scrollToContainer(config.scrollToContainerId);
							}
						})
					)
			);
		}
	}

	private scrollToContainer(containerId: string): void {
		const container = this.extendedWorkoutExerciseContainers()?.find((m) => m.originalContainer.id === containerId);

		if (container) {
			this.dependencies.windowScrollService.scrollToElement({
				elementId: container.domId
			});
		}
	}

	private createSession(data: {
		readonly user: UserDb.IUserItem;
		readonly stateItems: readonly SessionDb.ISessionStateItem[];
		readonly workout: WorkoutDb.IWorkoutItem | undefined;
	}): void {
		super.subscribeToObservable(
			this.dependencies.httpService
				.create<SessionDb.ISessionItem, SessionDb.IBaseSessionItem>({
					type: 'session',
					createModel: {
						name: `Workout session`,
						notes: undefined,
						userId: data.user.id,
						sessionType: 'workout',
						date: this.dependencies.timeService.toISODate(this.dependencies.timeService.now()),
						state: data.stateItems,
						workout: data.workout
					}
				})
				.pipe(
					map((response) => {
						super.notifyCreated('Session');
						this.selectedSessionItem.set(response.data);
						this.openEditSessionDialog();
					})
				)
		);
	}

	private openEditSessionDialog(): void {
		const session = this.selectedSessionItem();

		if (!session) {
			return;
		}
		let subtitle: string = sessionsHelper.localizeSessionType(session.sessionType);

		if (session.sessionType === 'workout' && session.workout) {
			subtitle = `${sessionsHelper.localizeSessionType(session.sessionType)} - ${session.workout.system.name}`;
		}

		this.dependencies.dialogService.openDialog({
			title: 'Edit session',
			subtitle: subtitle,
			size: 'large',
			template: () => this.editSessionTemplate
		});
	}

	private openEditDialog(data: {
		readonly workout: WorkoutDb.IWorkoutItem;
		readonly getProp: (workout: WorkoutDb.IWorkoutItem) => string | undefined;
		readonly setProp: (workout: WorkoutDb.IWorkoutItem, value: string | undefined) => void;
		readonly title: string;
		readonly fieldName?: string;
		readonly fieldType: DialogFormFieldType;
	}): void {
		this.dependencies.dialogService.openFormDialog<EditTextDialog>({
			title: data.title,
			subtitle: data.workout.system.name,
			size: 'auto',
			fields: [
				{
					propertyName: 'text',
					required: false,
					title: data.fieldName,
					type: data.fieldType
				}
			],
			item: {
				text: data.getProp(data.workout)
			},
			save: (item) => {
				this.workout.update((currentWorkout) => {
					if (currentWorkout) {
						data.setProp(data.workout, item.text);
					}
					return cloneHelper.clone(currentWorkout);
				});

				this._workoutSave.next({});
			}
		});
	}
}
